import { QueryClient, type QueryCacheNotifyEvent, type MutationCacheNotifyEvent } from '@tanstack/react-query';
import { logger } from '../lib/logger';

/**
 * Gehärteter QueryClient mit globalen Logging-Handlers.
 * Wird vom Integrator in main.tsx eingebunden.
 *
 * Default-Optionen:
 *  - staleTime: 5 Minuten
 *  - retry: 1 (statt 3, reduziert Lärm)
 *  - refetchOnWindowFocus: false (verhindert ungewollte Reloads)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function keyToString(key: unknown): string {
  try {
    if (Array.isArray(key)) return key.map((k) => (typeof k === 'object' ? JSON.stringify(k) : String(k))).join('/');
    return String(key);
  } catch {
    return '<unserializable-key>';
  }
}

queryClient.getQueryCache().subscribe((event: QueryCacheNotifyEvent) => {
  const query = event.query;
  if (!query) return;
  const queryKey = query.queryKey;
  const keyLabel = keyToString(queryKey);

  switch (event.type) {
    case 'added': {
      logger.debug('query', `observe ${keyLabel}`, { queryKey: queryKey as unknown as Record<string, unknown> });
      break;
    }
    case 'removed': {
      logger.debug('query', `remove ${keyLabel}`, { queryKey: queryKey as unknown as Record<string, unknown> });
      break;
    }
    case 'updated': {
      const state = query.state;
      if (state.fetchStatus === 'idle' && state.status === 'success') {
        logger.debug('query', `✓ ${keyLabel}`, {
          queryKey: queryKey as unknown as Record<string, unknown>,
          dataUpdatedAt: state.dataUpdatedAt,
        });
      } else if (state.status === 'error') {
        const err = state.error;
        const errPayload =
          err instanceof Error
            ? { name: err.name, message: err.message }
            : { error: String(err) };
        logger.warn('query', `✗ ${keyLabel}`, {
          queryKey: queryKey as unknown as Record<string, unknown>,
          ...errPayload,
        });
      }
      break;
    }
    default:
      break;
  }
});

queryClient.getMutationCache().subscribe((event: MutationCacheNotifyEvent) => {
  const mutation = event.mutation;
  if (!mutation) return;
  if (event.type !== 'updated') return;

  const state = mutation.state;
  const keyLabel =
    mutation.options.mutationKey !== undefined
      ? keyToString(mutation.options.mutationKey)
      : '<anonymous>';
  const variables = state.variables as Record<string, unknown> | undefined;

  if (state.status === 'success') {
    logger.info('mutation', `✓ ${keyLabel}`, {
      mutationKey: mutation.options.mutationKey as unknown as Record<string, unknown> | undefined,
      variables,
      response: state.data as Record<string, unknown> | undefined,
    });
  } else if (state.status === 'error') {
    const err = state.error;
    const errPayload =
      err instanceof Error
        ? { name: err.name, message: err.message, stack: err.stack }
        : { error: String(err) };
    logger.error('mutation', `✗ ${keyLabel}`, {
      mutationKey: mutation.options.mutationKey as unknown as Record<string, unknown> | undefined,
      variables,
      ...errPayload,
    });
  }
});
